import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users,
  Globe,
  Clock,
  FileText,
  MousePointerClick,
  Download,
  RefreshCw,
  LogOut,
  Lock,
  ArrowLeft,
  CheckCircle2,
  CircleDashed,
  Fingerprint,
  Search,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { getPixelStatus } from "../pixels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSecs = (s) => {
  if (!s && s !== 0) return "-";
  const secs = Math.round(s);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
};

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/* ---------- Login gate ---------- */
const LoginGate = ({ onLogin }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/dashboard/login`, { password });
      onLogin(res.data.key);
    } catch {
      setError("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-black px-5">
      <Card className="w-full max-w-sm border-white/10 bg-[#0a0f0b] text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock size={18} className="text-[#16E36F]" />
            Dashboard Access
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <Input
              type="password"
              placeholder="Enter dashboard password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border-white/15 bg-black text-white placeholder:text-neutral-500"
              data-testid="dashboard-password-input"
            />
            {error && <p className="text-sm text-red-400">{error}</p>}
            <Button
              type="submit"
              disabled={loading || !password}
              className="w-full bg-[#16E36F] font-semibold text-black hover:bg-[#2df584]"
              data-testid="dashboard-login-btn"
            >
              {loading ? "Checking..." : "Unlock Dashboard"}
            </Button>
            <Link
              to="/"
              className="flex items-center justify-center gap-1 text-xs text-neutral-500 hover:text-[#16E36F]"
            >
              <ArrowLeft size={12} /> Back to site
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

/* ---------- Stat card ---------- */
const StatCard = ({ icon: Icon, label, value, sub }) => (
  <Card className="border-white/10 bg-[#0a0f0b] text-white">
    <CardContent className="p-5">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-neutral-500">
        <Icon size={14} className="text-[#16E36F]" />
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold">{value}</div>
      {sub && <div className="mt-1 text-xs text-neutral-500">{sub}</div>}
    </CardContent>
  </Card>
);

/* ---------- Horizontal bar ---------- */
const Bar = ({ label, value, max, suffix }) => (
  <div className="mb-3">
    <div className="mb-1 flex items-center justify-between text-sm">
      <span className="text-neutral-300">{label}</span>
      <span className="font-semibold text-white">
        {value}
        {suffix || ""}
      </span>
    </div>
    <div className="h-2 rounded-full bg-white/5">
      <div
        className="h-2 rounded-full bg-[#16E36F] transition-all duration-500"
        style={{ width: `${max > 0 ? Math.max((value / max) * 100, 2) : 2}%` }}
      />
    </div>
  </div>
);

const Dashboard = () => {
  const [key, setKey] = useState(
    () => sessionStorage.getItem("tmu_dash_key") || null
  );
  const [summary, setSummary] = useState(null);
  const [applications, setApplications] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [whopPeople, setWhopPeople] = useState([]);
  const [whopTotal, setWhopTotal] = useState(0);
  const [whopProspects, setWhopProspects] = useState(0);
  const [whopSearch, setWhopSearch] = useState("");
  const [whopAudience, setWhopAudience] = useState("all");
  const [whopSources, setWhopSources] = useState([]);
  const [whopLoading, setWhopLoading] = useState(false);
  const [whopSyncing, setWhopSyncing] = useState(false);
  const whopLoadedRef = useRef(false);

  // Calendly UTM source report
  const [srcReport, setSrcReport] = useState(null);
  const [srcLoading, setSrcLoading] = useState(false);
  const [srcScope, setSrcScope] = useState("tmu");
  const [srcStatus, setSrcStatus] = useState("active");
  const [srcFrom, setSrcFrom] = useState("");
  const [srcTo, setSrcTo] = useState("");

  const headers = { "X-Dashboard-Key": key };

  const loadSources = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/calendly/sources`, {
        headers: { "X-Dashboard-Key": key },
      });
      setSrcReport(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, [key]);

  const buildSources = async () => {
    setSrcLoading(true);
    try {
      const params = new URLSearchParams({ status: srcStatus, scope: srcScope });
      if (srcFrom) params.set("from_date", srcFrom);
      if (srcTo) params.set("to_date", srcTo);
      await axios.post(`${API}/calendly/sources/build?${params.toString()}`, {}, {
        headers: { "X-Dashboard-Key": key },
      });
      // poll until ready (up to ~2 min)
      for (let i = 0; i < 40; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        const d = await loadSources();
        if (d && d.status === "ready") break;
      }
    } catch (e) {
      // ignore
    }
    setSrcLoading(false);
  };

  const fetchData = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    setError(null);
    try {
      const [s, a, l] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { headers: { "X-Dashboard-Key": key } }),
        axios.get(`${API}/applications`, { headers: { "X-Dashboard-Key": key } }),
        axios.get(`${API}/leads`, { headers: { "X-Dashboard-Key": key } }),
      ]);
      setSummary(s.data);
      setApplications(a.data);
      setLeads(l.data);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("tmu_dash_key");
        setKey(null);
      } else {
        setError("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    // Prevent search engines from indexing this private page
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive, nosnippet";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "•";
    return () => {
      if (meta.parentNode) meta.parentNode.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    fetchData();
    loadSources();
  }, [fetchData, loadSources]);

  const onLogin = (k) => {
    sessionStorage.setItem("tmu_dash_key", k);
    setKey(k);
  };

  const fetchWhopPeople = useCallback(
    async (q = "", aud = "all") => {
      if (!key) return;
      setWhopLoading(true);
      try {
        const res = await axios.get(`${API}/whop/people`, {
          headers: { "X-Dashboard-Key": key },
          params: {
            query: q || undefined,
            audience: aud && aud !== "all" ? aud : undefined,
            limit: 1000,
          },
        });
        setWhopPeople(res.data.people || []);
        setWhopTotal(res.data.total || 0);
        setWhopProspects(res.data.prospects || 0);
        whopLoadedRef.current = true;
      } catch {
        /* silent - non-critical panel */
      } finally {
        setWhopLoading(false);
      }
    },
    [key]
  );

  const fetchWhopSources = useCallback(async () => {
    if (!key) return;
    try {
      const res = await axios.get(`${API}/whop/sources`, {
        headers: { "X-Dashboard-Key": key },
      });
      setWhopSources(res.data.sources || []);
    } catch {
      /* silent */
    }
  }, [key]);

  const exportWhopCSV = async () => {    if (!key) return;
    try {
      const res = await axios.get(`${API}/whop/people`, {
        headers: { "X-Dashboard-Key": key },
        params: {
          query: whopSearch || undefined,
          audience: whopAudience !== "all" ? whopAudience : undefined,
          limit: 1000,
        },
      });
      const people = res.data.people || [];
      const cols = [
        "email", "phone", "name", "whop_username", "segment", "country",
        "city", "source_type", "utm_source", "whop_ecosystem_spend_usd",
        "purchase_count", "first_seen_at", "last_seen_at",
      ];
      const rows = people.map((p) => [
        p.email, p.phone, p.name, p.whop_username, p.audience, p.country,
        p.city, p.source_type, p.utm_source, p.ltv, p.purchase_count,
        p.first_seen_at, p.last_seen_at,
      ]);
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const csv = [cols.join(","), ...rows.map((r) => r.map(esc).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const seg = whopAudience === "all" ? "all" : whopAudience;
      link.download = `tmu-identified-${seg}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      /* silent */
    }
  };

  const syncWhopPeople = async () => {
    if (!key) return;
    setWhopSyncing(true);
    try {
      await axios.post(
        `${API}/whop/sync-people`,
        {},
        { headers: { "X-Dashboard-Key": key } }
      );
      await fetchWhopPeople(whopSearch, whopAudience);
      await fetchWhopSources();
    } catch {
      /* silent */
    } finally {
      setWhopSyncing(false);
    }
  };

  const backfillAlerts = async () => {
    if (!key) return;
    if (
      !window.confirm(
        "This posts an @everyone Discord alert for every not-yet-alerted paid-ad prospect (sent gradually). Continue?"
      )
    )
      return;
    try {
      const res = await axios.post(
        `${API}/whop/backfill-alerts`,
        {},
        { headers: { "X-Dashboard-Key": key } }
      );
      window.alert(
        `Backfill started — ~${res.data.pending_estimate} alerts will post to Discord gradually.`
      );
    } catch {
      window.alert("Could not start backfill.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("tmu_dash_key");
    setKey(null);
  };

  const exportCSV = () => {
    const cols = [
      "full_name",
      "email",
      "phone",
      "trading_experience",
      "trading_goals",
      "income_goal",
      "portfolio_size",
      "start_timeline",
      "call_date",
      "call_time",
      "utm_source",
      "utm_campaign",
      "submitted_at",
    ];
    const rows = applications.map((a) => [
      a.full_name,
      a.email,
      a.phone,
      a.trading_experience,
      a.trading_goals,
      a.income_goal,
      a.portfolio_size,
      a.start_timeline,
      a.scheduled_call?.date || "",
      a.scheduled_call?.time || "",
      a.utm?.utm_source || "direct",
      a.utm?.utm_campaign || "",
      a.submitted_at,
    ]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [cols.join(","), ...rows.map((r) => r.map(esc).join(","))].join(
      "\n"
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `tmu-applications-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (!key) return <LoginGate onLogin={onLogin} />;

  const t = summary?.totals || {};
  const scroll = summary?.scroll_depth || {};
  const maxScroll = Math.max(...[25, 50, 75, 100].map((m) => scroll[String(m)] || 0), 1);
  const funnel = summary?.funnel || [];
  const maxFunnel = Math.max(...funnel.map((f) => f.viewed), 1);
  const countries = summary?.countries || [];
  const maxCountry = Math.max(...countries.map((c) => c.visitors), 1);
  const pixels = getPixelStatus();

  return (
    <div className="min-h-screen bg-black px-5 py-8 text-white">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">
              <span className="text-[#16E36F]">TMU</span> Analytics Dashboard
            </h1>
            <p className="mt-1 text-sm text-neutral-500">
              Visitors, funnel performance & applications
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white"
              data-testid="dashboard-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </Button>
            <Link to="/">
              <Button
                variant="outline"
                size="sm"
                className="border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white"
              >
                <ArrowLeft size={14} /> Site
              </Button>
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white"
            >
              <LogOut size={14} /> Logout
            </Button>
          </div>
        </div>

        {error && <p className="mb-4 text-sm text-red-400">{error}</p>}

        {/* Stat cards */}
        <div className="mb-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatCard icon={Users} label="Visitors" value={t.visitors ?? "-"} sub={`${t.sessions ?? 0} sessions`} />
          <StatCard icon={MousePointerClick} label="Page Views" value={t.page_views ?? "-"} />
          <StatCard icon={Clock} label="Avg Time on Page" value={fmtSecs(t.avg_time_on_page_seconds)} />
          <StatCard icon={FileText} label="Applications" value={t.applications ?? "-"} sub={`${t.form_submissions ?? 0} unique submitters`} />
          <StatCard icon={Globe} label="Countries" value={countries.length} />
        </div>

        <Tabs
          defaultValue="overview"
          onValueChange={(v) => {
            if (v === "identified" && !whopLoadedRef.current) {
              fetchWhopPeople();
              fetchWhopSources();
            }
          }}
        >
          <TabsList className="mb-6 bg-[#0a0f0b]">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">
              Applications ({applications.length})
            </TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">
              Leads ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="identified" data-testid="tab-identified">
              Identified Visitors
            </TabsTrigger>
            <TabsTrigger value="pixels" data-testid="tab-pixels">Ad Pixels</TabsTrigger>
            <TabsTrigger value="sources" data-testid="tab-sources">Call Sources</TabsTrigger>
          </TabsList>

          {/* ---------- OVERVIEW ---------- */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Form funnel */}
              <Card className="border-white/10 bg-[#0a0f0b] text-white">
                <CardHeader>
                  <CardTitle className="text-base">Form Funnel & Drop-off</CardTitle>
                </CardHeader>
                <CardContent>
                  {funnel.map((f) => (
                    <div key={f.step} className="mb-5">
                      <div className="mb-1 flex items-center justify-between text-sm">
                        <span className="text-neutral-300">
                          <span className="mr-1.5 font-semibold text-[#16E36F]">
                            {f.step}.
                          </span>
                          {f.title}
                        </span>
                        <span className="text-xs text-neutral-500">
                          avg {fmtSecs(f.avg_seconds)}
                        </span>
                      </div>
                      <div className="h-6 rounded bg-white/5">
                        <div
                          className="flex h-6 items-center rounded bg-[#16E36F]/80 px-2 text-xs font-bold text-black transition-all duration-500"
                          style={{
                            width: `${Math.max((f.viewed / maxFunnel) * 100, 6)}%`,
                          }}
                        >
                          {f.viewed}
                        </div>
                      </div>
                      <div className="mt-1 flex justify-between text-[11px] text-neutral-500">
                        <span>{f.completed} completed</span>
                        {f.step > 1 && (
                          <span className={f.drop_off_percent > 50 ? "text-red-400" : ""}>
                            {f.drop_off_percent}% drop-off
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <div className="space-y-6">
                {/* Scroll depth */}
                <Card className="border-white/10 bg-[#0a0f0b] text-white">
                  <CardHeader>
                    <CardTitle className="text-base">Scroll Depth (visitors reached)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {[25, 50, 75, 100].map((m) => (
                      <Bar
                        key={m}
                        label={`${m}% of page`}
                        value={scroll[String(m)] || 0}
                        max={maxScroll}
                      />
                    ))}
                  </CardContent>
                </Card>

                {/* Countries */}
                <Card className="border-white/10 bg-[#0a0f0b] text-white">
                  <CardHeader>
                    <CardTitle className="text-base">Visitor Countries</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {countries.length === 0 && (
                      <p className="text-sm text-neutral-500">No visitors yet</p>
                    )}
                    {countries.slice(0, 8).map((c) => (
                      <Bar
                        key={c.country}
                        label={`${c.country}${c.country_code ? ` (${c.country_code})` : ""}`}
                        value={c.visitors}
                        max={maxCountry}
                      />
                    ))}
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Traffic sources */}
            <Card className="mt-6 border-white/10 bg-[#0a0f0b] text-white">
              <CardHeader>
                <CardTitle className="text-base">
                  Traffic Sources — which ads produce applications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-neutral-400">Source</TableHead>
                      <TableHead className="text-neutral-400">Campaign</TableHead>
                      <TableHead className="text-neutral-400">Medium</TableHead>
                      <TableHead className="text-neutral-400">Visitors</TableHead>
                      <TableHead className="text-neutral-400">Applications</TableHead>
                      <TableHead className="text-neutral-400">Conv. Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(summary?.sources || []).length === 0 && (
                      <TableRow className="border-white/5">
                        <TableCell colSpan={6} className="text-center text-neutral-500">
                          No traffic data yet
                        </TableCell>
                      </TableRow>
                    )}
                    {(summary?.sources || []).map((s, i) => (
                      <TableRow key={i} className="border-white/5 hover:bg-white/5">
                        <TableCell className="font-medium capitalize text-white">
                          {s.source}
                        </TableCell>
                        <TableCell>{s.campaign}</TableCell>
                        <TableCell>{s.medium}</TableCell>
                        <TableCell>{s.visitors}</TableCell>
                        <TableCell>
                          <span className={s.applications > 0 ? "font-semibold text-[#16E36F]" : ""}>
                            {s.applications}
                          </span>
                        </TableCell>
                        <TableCell>{s.conversion_rate}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Pages */}
            <Card className="mt-6 border-white/10 bg-[#0a0f0b] text-white">
              <CardHeader>
                <CardTitle className="text-base">Pages</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-neutral-400">Page</TableHead>
                      <TableHead className="text-neutral-400">Views</TableHead>
                      <TableHead className="text-neutral-400">Unique Visitors</TableHead>
                      <TableHead className="text-neutral-400">Avg Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(summary?.pages || []).map((p) => (
                      <TableRow key={p.page} className="border-white/5 hover:bg-white/5">
                        <TableCell className="font-medium text-white">{p.page}</TableCell>
                        <TableCell>{p.views}</TableCell>
                        <TableCell>{p.unique_visitors}</TableCell>
                        <TableCell>{fmtSecs(p.avg_time_seconds)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- APPLICATIONS ---------- */}
          <TabsContent value="applications">
            <Card className="border-white/10 bg-[#0a0f0b] text-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">
                  Submitted Applications ({applications.length})
                </CardTitle>
                <Button
                  size="sm"
                  onClick={exportCSV}
                  disabled={applications.length === 0}
                  className="bg-[#16E36F] font-semibold text-black hover:bg-[#2df584]"
                  data-testid="export-csv-btn"
                >
                  <Download size={14} /> Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-neutral-400">Name</TableHead>
                        <TableHead className="text-neutral-400">Email</TableHead>
                        <TableHead className="text-neutral-400">Phone</TableHead>
                        <TableHead className="text-neutral-400">Experience</TableHead>
                        <TableHead className="text-neutral-400">Goal</TableHead>
                        <TableHead className="text-neutral-400">Income Goal</TableHead>
                        <TableHead className="text-neutral-400">Portfolio</TableHead>
                        <TableHead className="text-neutral-400">Start</TableHead>
                        <TableHead className="text-neutral-400">Call</TableHead>
                        <TableHead className="text-neutral-400">Source</TableHead>
                        <TableHead className="text-neutral-400">Submitted</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {applications.length === 0 && (
                        <TableRow className="border-white/5">
                          <TableCell colSpan={11} className="text-center text-neutral-500">
                            No applications yet
                          </TableCell>
                        </TableRow>
                      )}
                      {applications.map((a) => (
                        <TableRow key={a.id} className="border-white/5 hover:bg-white/5">
                          <TableCell className="font-medium text-white">{a.full_name}</TableCell>
                          <TableCell>{a.email}</TableCell>
                          <TableCell className="whitespace-nowrap">{a.phone}</TableCell>
                          <TableCell>{a.trading_experience || "-"}</TableCell>
                          <TableCell>{a.trading_goals || "-"}</TableCell>
                          <TableCell>{a.income_goal || "-"}</TableCell>
                          <TableCell>{a.portfolio_size || "-"}</TableCell>
                          <TableCell>{a.start_timeline || "-"}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {a.scheduled_call
                              ? `${a.scheduled_call.date} ${a.scheduled_call.time}`
                              : "-"}
                          </TableCell>
                          <TableCell className="capitalize">
                            {a.utm?.utm_source || "direct"}
                            {a.utm?.utm_campaign ? (
                              <span className="block text-[11px] text-neutral-500">
                                {a.utm.utm_campaign}
                              </span>
                            ) : null}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-neutral-400">
                            {fmtDate(a.submitted_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- LEADS ---------- */}
          <TabsContent value="leads">
            <Card className="border-white/10 bg-[#0a0f0b] text-white">
              <CardHeader>
                <CardTitle className="text-base">
                  Leads — captured from the first answered question ({leads.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-neutral-400">Name</TableHead>
                        <TableHead className="text-neutral-400">Email</TableHead>
                        <TableHead className="text-neutral-400">Phone</TableHead>
                        <TableHead className="text-neutral-400">Progress</TableHead>
                        <TableHead className="text-neutral-400">Status</TableHead>
                        <TableHead className="text-neutral-400">Discord</TableHead>
                        <TableHead className="text-neutral-400">Source</TableHead>
                        <TableHead className="text-neutral-400">Created</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {leads.length === 0 && (
                        <TableRow className="border-white/5">
                          <TableCell colSpan={8} className="text-center text-neutral-500">
                            No leads yet
                          </TableCell>
                        </TableRow>
                      )}
                      {leads.map((l) => (
                        <TableRow key={l.id} className="border-white/5 hover:bg-white/5">
                          <TableCell className="font-medium text-white">
                            {l.full_name || "-"}
                          </TableCell>
                          <TableCell>{l.email || "-"}</TableCell>
                          <TableCell className="whitespace-nowrap">{l.phone || "-"}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-16 rounded-full bg-white/10">
                                <div
                                  className="h-1.5 rounded-full bg-[#16E36F]"
                                  style={{ width: `${Math.min(((l.last_question || 1) / 9) * 100, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs text-neutral-400">
                                Q{l.last_question || 1}/9
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {l.status === "completed" ? (
                              <Badge className="bg-[#16E36F]/15 text-[#16E36F] hover:bg-[#16E36F]/15">
                                Completed
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-500/15 text-amber-400 hover:bg-amber-500/15">
                                In progress
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {l.discord_notified ? (
                              <CheckCircle2 size={16} className="text-[#16E36F]" />
                            ) : (
                              <CircleDashed size={16} className="text-neutral-500" />
                            )}
                          </TableCell>
                          <TableCell className="capitalize">
                            {l.utm?.utm_source || "direct"}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-neutral-400">
                            {fmtDate(l.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- IDENTIFIED VISITORS (WHOP) ---------- */}
          <TabsContent value="identified">
            <Card className="border-white/10 bg-[#0a0f0b] text-white">
              <CardHeader>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Fingerprint size={16} className="text-[#16E36F]" />
                      Identified Visitors — paired to a Whop user via the pixel
                    </CardTitle>
                    <p className="mt-1 text-xs text-neutral-500">
                      {whopTotal.toLocaleString()} identified ·{" "}
                      <span className="text-[#16E36F]">
                        {whopProspects.toLocaleString()} prospects
                      </span>{" "}
                      (not yet in your ecosystem) · showing {whopPeople.length} ·
                      auto-syncs every 15 min
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="flex rounded-md border border-white/15 p-0.5">
                      {[
                        ["all", "All"],
                        ["prospect", "Prospects"],
                        ["member", "Members"],
                        ["customer", "Customers"],
                      ].map(([val, lbl]) => (
                        <button
                          key={val}
                          onClick={() => {
                            setWhopAudience(val);
                            fetchWhopPeople(whopSearch, val);
                          }}
                          className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                            whopAudience === val
                              ? "bg-[#16E36F] text-black"
                              : "text-neutral-400 hover:text-white"
                          }`}
                          data-testid={`whop-filter-${val}`}
                        >
                          {lbl}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500"
                      />
                      <Input
                        value={whopSearch}
                        onChange={(e) => setWhopSearch(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") fetchWhopPeople(whopSearch, whopAudience);
                        }}
                        placeholder="Search email, phone, name…"
                        className="h-9 w-52 border-white/15 bg-black pl-8 text-white placeholder:text-neutral-500"
                        data-testid="whop-search-input"
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fetchWhopPeople(whopSearch, whopAudience)}
                      disabled={whopLoading}
                      className="border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white"
                      data-testid="whop-search-btn"
                    >
                      <RefreshCw size={14} className={whopLoading ? "animate-spin" : ""} />
                      Search
                    </Button>
                    <Button
                      size="sm"
                      onClick={syncWhopPeople}
                      disabled={whopSyncing}
                      className="bg-[#16E36F] font-semibold text-black hover:bg-[#2df584]"
                      data-testid="whop-sync-btn"
                    >
                      {whopSyncing ? "Syncing…" : "Sync now"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={exportWhopCSV}
                      disabled={whopPeople.length === 0}
                      className="border-white/15 bg-transparent text-white hover:bg-white/10 hover:text-white"
                      data-testid="whop-export-btn"
                    >
                      <Download size={14} /> Export
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={backfillAlerts}
                      className="border-amber-500/40 bg-transparent text-amber-400 hover:bg-amber-500/10 hover:text-amber-300"
                      data-testid="whop-backfill-btn"
                    >
                      Backfill alerts
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {whopSources.length > 0 && (
                  <div className="mb-6 rounded-lg border border-white/10 bg-black/40 p-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                      <MousePointerClick size={15} className="text-[#16E36F]" />
                      Match to Ads — sources that bring identifiable Whop users
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="text-neutral-400">Source</TableHead>
                            <TableHead className="text-neutral-400">Campaign / UTM</TableHead>
                            <TableHead className="text-neutral-400">Identified</TableHead>
                            <TableHead className="text-neutral-400">Prospects</TableHead>
                            <TableHead className="text-neutral-400">Customers</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {whopSources.map((s, i) => (
                            <TableRow key={i} className="border-white/5 hover:bg-white/5">
                              <TableCell className="font-medium capitalize text-white">
                                {s.source_type || "unknown"}
                              </TableCell>
                              <TableCell>{s.utm_source || "-"}</TableCell>
                              <TableCell>{s.identified}</TableCell>
                              <TableCell className="text-amber-400">{s.prospects}</TableCell>
                              <TableCell className="text-[#16E36F]">{s.customers}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-neutral-400">Email</TableHead>
                        <TableHead className="text-neutral-400">Phone</TableHead>
                        <TableHead className="text-neutral-400">Name / Whop</TableHead>
                        <TableHead className="text-neutral-400">Segment</TableHead>
                        <TableHead className="text-neutral-400">Country</TableHead>
                        <TableHead className="text-neutral-400">Source</TableHead>
                        <TableHead className="text-neutral-400">LTV</TableHead>
                        <TableHead className="text-neutral-400">Purchases</TableHead>
                        <TableHead className="text-neutral-400">Last Seen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {whopPeople.length === 0 && (
                        <TableRow className="border-white/5">
                          <TableCell colSpan={9} className="text-center text-neutral-500">
                            {whopLoading ? "Loading…" : "No identified visitors yet"}
                          </TableCell>
                        </TableRow>
                      )}
                      {whopPeople.map((p) => (
                        <TableRow key={p.id} className="border-white/5 hover:bg-white/5">
                          <TableCell className="font-medium text-white">
                            {p.email || "-"}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{p.phone || "-"}</TableCell>
                          <TableCell>
                            {p.name || "-"}
                            {p.whop_username && (
                              <span className="block text-[11px] text-neutral-500">
                                @{p.whop_username}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {p.audience === "customer" ? (
                              <Badge className="bg-[#16E36F]/15 text-[#16E36F] hover:bg-[#16E36F]/15">
                                Customer
                              </Badge>
                            ) : p.audience === "member" ? (
                              <Badge className="bg-sky-500/15 text-sky-400 hover:bg-sky-500/15">
                                Member
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-500/15 text-amber-400 hover:bg-amber-500/15">
                                Prospect
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {p.country || "-"}
                            {p.city && (
                              <span className="block text-[11px] text-neutral-500">
                                {p.city}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="capitalize">
                            {p.source_type || "-"}
                            {p.utm_source && (
                              <span className="block text-[11px] text-neutral-500">
                                {p.utm_source}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {p.ltv ? `$${Number(p.ltv).toLocaleString()}` : "-"}
                          </TableCell>
                          <TableCell>{p.purchase_count ?? 0}</TableCell>
                          <TableCell className="whitespace-nowrap text-neutral-400">
                            {fmtDate(p.last_seen_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- PIXELS ---------- */}
          <TabsContent value="pixels">
            <Card className="border-white/10 bg-[#0a0f0b] text-white">
              <CardHeader>
                <CardTitle className="text-base">Ad Platform Pixels</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-5 text-sm text-neutral-400">
                  Every tracked event (page views, form steps, submissions, CTA
                  clicks) is automatically mirrored to all configured platforms.
                  Add the account IDs to{" "}
                  <code className="rounded bg-white/10 px-1.5 py-0.5 text-xs">
                    frontend/.env
                  </code>{" "}
                  and the pixels activate instantly - no code changes needed.
                </p>
                <div className="space-y-3">
                  {pixels.map((p) => (
                    <div
                      key={p.name}
                      className="flex items-center justify-between rounded-lg border border-white/10 p-4"
                    >
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="mt-0.5 font-mono text-xs text-neutral-500">
                          {p.envVar}
                        </div>
                      </div>
                      {p.configured ? (
                        <Badge className="bg-[#16E36F]/15 text-[#16E36F] hover:bg-[#16E36F]/15">
                          <CheckCircle2 size={12} className="mr-1" /> Active
                        </Badge>
                      ) : (
                        <Badge className="bg-white/10 text-neutral-400 hover:bg-white/10">
                          <CircleDashed size={12} className="mr-1" /> Awaiting ID
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-lg border border-[#16E36F]/25 bg-[#16E36F]/5 p-4 text-sm text-neutral-300">
                  <strong className="text-[#16E36F]">Event mapping:</strong>{" "}
                  GA4 receives all events; Google Ads fires a conversion +
                  generate_lead on form submit; Taboola fires page_view,
                  start_application (step 1 view), cta_click and lead (submit);
                  Whop fires page, schedule (call step completed) and lead
                  (submit).
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sources">
            <Card className="border-white/10 bg-[#0a0f0b] text-white">
              <CardHeader>
                <CardTitle className="text-base">
                  Booked Call Sources (Calendly UTM)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="mb-4 text-sm text-neutral-400">
                  Where your booked calls actually came from, pulled live from the
                  Calendly API (invitee UTM tracking). "Actual calls" = active
                  bookings.
                </p>
                <div className="mb-5 flex flex-wrap items-end gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-neutral-500">Scope</label>
                    <select
                      data-testid="sources-scope"
                      value={srcScope}
                      onChange={(e) => setSrcScope(e.target.value)}
                      className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm"
                    >
                      <option value="tmu">Joel — TMU enrollment</option>
                      <option value="all">All calendars</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-neutral-500">Status</label>
                    <select
                      data-testid="sources-status"
                      value={srcStatus}
                      onChange={(e) => setSrcStatus(e.target.value)}
                      className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm"
                    >
                      <option value="active">Active</option>
                      <option value="canceled">Canceled</option>
                      <option value="all">All</option>
                    </select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-neutral-500">From</label>
                    <input
                      type="date"
                      data-testid="sources-from"
                      value={srcFrom}
                      onChange={(e) => setSrcFrom(e.target.value)}
                      className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-neutral-500">To</label>
                    <input
                      type="date"
                      data-testid="sources-to"
                      value={srcTo}
                      onChange={(e) => setSrcTo(e.target.value)}
                      className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm"
                    />
                  </div>
                  <Button
                    data-testid="sources-build-btn"
                    onClick={buildSources}
                    disabled={srcLoading}
                    className="bg-[#16E36F] text-black hover:bg-[#13c561]"
                  >
                    {srcLoading ? "Building…" : "Build report"}
                  </Button>
                </div>

                {srcReport && srcReport.status === "ready" && (
                  <div data-testid="sources-report">
                    <div className="mb-4 text-sm text-neutral-300">
                      <span className="text-2xl font-semibold text-white">
                        {srcReport.total_calls}
                      </span>{" "}
                      calls · scope{" "}
                      <span className="text-[#16E36F]">{srcReport.scope}</span> ·
                      status {srcReport.booking_status}
                    </div>
                    <div className="grid gap-6 md:grid-cols-3">
                      {[
                        ["By source", srcReport.by_source],
                        ["By medium", srcReport.by_medium],
                        ["By campaign", srcReport.by_campaign],
                      ].map(([title, rows]) => (
                        <div key={title}>
                          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
                            {title}
                          </div>
                          <div className="space-y-1">
                            {(rows || []).map((r) => {
                              const pct = srcReport.total_calls
                                ? Math.round((r.count / srcReport.total_calls) * 100)
                                : 0;
                              return (
                                <div
                                  key={r.key}
                                  className="flex items-center justify-between rounded border border-white/5 px-3 py-1.5 text-sm"
                                >
                                  <span className="truncate pr-2 text-neutral-300">
                                    {r.key}
                                  </span>
                                  <span className="whitespace-nowrap font-medium">
                                    {r.count}{" "}
                                    <span className="text-neutral-500">({pct}%)</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-xs text-neutral-500">
                      Built {srcReport.built_at}
                    </div>
                  </div>
                )}
                {srcReport && srcReport.status === "building" && (
                  <p className="text-sm text-[#16E36F]">Building report…</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Dashboard;
